import isEmpty from "lodash/isEmpty";
import { Response } from "node-fetch";
import queryString from "query-string";
import { SyntheticEvent, useEffect, useReducer } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";

import { DataLoading, User } from "../../types";
import { VendorUtilities } from "../../utilities/VendorUtilities";
import Alert from "../shared/Alert";
import { Button } from "../shared/Button";
import ExternalLink from "../shared/ExternalLink";
import Footer from "../shared/Footer";
import ReplicatedInfo from "./ReplicatedInfo";
import TextInput from "../shared/TextInput";

interface SignupProps {
  clearSignupValidationErrors: () => void;
  dataLoading: DataLoading;
  handleGoogleSignup: (isBuildersOnly: boolean) => void;
  handleSignUp: ({
    email,
    isBuildersOnly
  }: {
    email: string;
    isBuildersOnly?: boolean;
  }) => Response;
  hasToken?: string;
  setLoadingToFalse: () => void;
  signupValidationErrors: {
    email?: {
      error: string;
    };
  };
  user: User;
  isBuildersOnly?: boolean;
}

interface SignupState {
  email?: string;
  emailError?: boolean;
  emailMessage?: string;
}

const Signup = ({
  clearSignupValidationErrors,
  dataLoading,
  handleGoogleSignup,
  handleSignUp,
  hasToken,
  setLoadingToFalse,
  signupValidationErrors,
  user,
  isBuildersOnly
}: SignupProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [state, setState] = useReducer(
    (prevState: SignupState, newState: SignupState) => ({
      ...prevState,
      ...newState
    }),
    {
      email: "",
      emailError: false,
      emailMessage: ""
    }
  );

  const clearErrors = () => {
    setState({
      emailError: false,
      emailMessage: ""
    });
  };

  const handleFormChange = (value: string) => {
    if (state.emailError) {
      clearErrors();
    }
    if (!isEmpty(signupValidationErrors)) {
      clearSignupValidationErrors();
    }
    setState({
      email: value
    });
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    clearErrors();

    const response = await handleSignUp({
      email: state.email.trim(),
      ...(isBuildersOnly ? { isBuildersOnly: true } : {})
    });

    if (response?.status === 201) {
      if (response.json) {
        const body: { token?: string } = await response.json();
        if (body.token) {
          navigate(`/signup/verify#${body.token}`);
          return;
        }
      }

      navigate("/signup/verify");
    }
  };

  const handleGoogleSignupClick = ev => {
    ev.preventDefault();
    handleGoogleSignup(isBuildersOnly ? true : false);
  };

  const buildErrors = (error: { email?: { error: string } }) => {
    if (error.email) {
      setState({
        emailError: true,
        emailMessage: error.email.error
      });
    }
  };

  useEffect(() => {
    if (signupValidationErrors) {
      buildErrors(signupValidationErrors);
    }
  }, [signupValidationErrors]);

  useEffect(() => {
    const parsedSearch = queryString.parse(location.search);
    const inviteSecret = parsedSearch?.invite || location.hash.slice(1);
    const replaceAccount = parsedSearch?.replace_account;
    if (inviteSecret || replaceAccount) {
      const inviteParams = inviteSecret ? `#${inviteSecret}` : "";
      const replaceAccountParams = replaceAccount
        ? `?replace_account=${replaceAccount}`
        : "";

      navigate(
        `/invite${replaceAccountParams}${
          inviteParams && replaceAccountParams && "&"
        }${inviteParams}`
      );
      return;
    }

    VendorUtilities.purgeVOneData();
    clearSignupValidationErrors();

    const { next } = params;

    // if user is logged in, redirect to the next page
    if (!!hasToken && user?.id) {
      navigate(`/${next || "apps"}`);
      return;
    }

    setLoadingToFalse();
  }, []);

  // if a user is already logged in
  // we return nothing while we wait for our useEffect to redirect them
  if (!!hasToken && user?.id) return null;

  return (
      <div className="h-full w-full flex flex-col overflow-y-auto">
      <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
        <ReplicatedInfo needsActivation={false} />
        <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 text-left auth-pages">
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            <p className="font-['Montserrat',sans-serif] font-bold text-indigo-500 text-[30px] !text-left max-lg:mt-[70px] leading-[1.4]">Start a trial</p>
          </div>
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            <p className="text-base font-normal text-gray-410 leading-[1.4] mt-3">
              Sign up and get access to the full Replicated platform for 21 days to
              build your technical proof-of-concept.
            </p>
            <div className="flex mt-[15px] mb-5">
              <a
                href="#"
                onClick={handleGoogleSignupClick}
                className="py-[10px] px-[25px] w-full text-base font-semibold rounded-[7px] border-2 border-solid border-hex-4285f4 text-hex-4285f4 bg-white hover:bg-hex-f3f7ff !flex items-center justify-center"
              >
                <span className="icon clickable google-g mr-3" />{" "}
                Continue with Google
              </a>
            </div>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-[100px] bg-gray-410" />
              <span className="text-sm font-semibold leading-none text-gray-410">or</span>
              <div className="h-px w-[100px] bg-gray-410" />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="FormSection-wrapper auth-pages">
                {state.emailError && (
                  <Alert variant="error" className="mb-1.5">
                    {state.emailMessage}
                  </Alert>
                )}
                <div className="mb-3">
                  <label htmlFor="companyEmail">Company Email</label>
                  <TextInput
                    name="companyEmail"
                    type="text"
                    placeholder="jack@example.com"
                    value={state.email}
                    onChangeValue={handleFormChange}
                  />
                </div>
              </div>
              <div className="FormButton-wrapper flex">
                <Button
                  kind="primary-purple"
                  className="flex-none mt-2.5 mb-5 w-full !text-lg"
                  disabled={dataLoading.signupLoading}
                >
                  {dataLoading.signupLoading ? "Loading..." : "Get Started"}
                </Button>
              </div>
              <div className="flex-none ml-3 text-sm items-center justify-center flex flex-col">
                <p className="text-gray-410 text-center">
                  Have an account?{" "}
                  <Link
                    to="/login"
                    className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                  >
                    Log in
                  </Link>
                </p>
              </div>
              <div className="flex-none ml-3 text-sm items-center justify-center flex flex-col">
                <p className="text-gray-410 text-center pt-5">
                  By clicking "Get Started", I agree to Replicated's{" "}
                  <ExternalLink
                    className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                    href="https://replicated.com/terms"
                  >
                    TOS
                  </ExternalLink>{" "}
                  and{" "}
                  <ExternalLink
                    className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                    href="https://replicated.com/privacy"
                  >
                    Privacy Policy
                  </ExternalLink>
                  .
                </p>
              </div>
            </form>
          </div>
          <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
